import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ReceiverAccountDetailsModal from "../../shared/ReceiverAccountDetailsModal";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import { ProfileContext } from "../../../../store/ProfileProvider";
import DisplayAmount from "../../shared/DisplayAmount";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Subaccount = {
  id: string;
  name: string;
};
type Merchant = {
  id: string;
  name: string;
};

type ReceiverAccount = {
  id: string;
  email: string;
  method_details: Object;
};

type PayoutProvider = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  status: string;
  amount: string;
  asset: string;
  created_at: string;
  note: string;
  merchant: Merchant;
  subaccount: Subaccount;
  transactions: any[];
  receiver_account: ReceiverAccount;
  payout_provider: PayoutProvider;
  cause: any;
  reference_tracking: any;
  method_name: string;
  crypto: boolean;
};

const ReceiverPayoutHistory: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
}> = ({ isLoading, tableData, page, pageSize, rowCount, setPaginationModel, currentLastKey }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isOpenReceiverAccountDetailModal, setIsOpenReceiverAccountDetailModal] =
    useState<boolean>(false);
  const [receiverAccountDetailToDisplay, setReceiverAccountDetailToDisplay] =
    useState<any>(null);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface PayoutStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: PayoutStatusItems[] = [
    { status: "awaiting", value: "awaiting", display: <Badge className={`${statusClasses} status-awaiting`}>Awaiting</Badge> },
    { status: "sent_mail", value: "sent_mail", display: <Badge className={`${statusClasses} status-sent_mail`}>Email Sent</Badge> },
    { status: "pending", value: "pending", display: <Badge className={`${statusClasses} status-pending`}>Pending</Badge> },
    { status: "unassessed", value: "unassessed", display: <Badge className={`${statusClasses} status-unassessed`}>Unassessed</Badge> },
    { status: "in_progress", value: "in_progress", display: <Badge className={`${statusClasses} status-in_progress`}>In Progress</Badge> },
    { status: "successful", value: "successful", display: <Badge className={`${statusClasses} status-successful`}>Completed</Badge> },
    { status: "cancelled", value: "cancelled", display: <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge> },
    { status: "failed", value: "failed", display: <Badge className={`${statusClasses} status-failed`}>Failed</Badge> },
    { status: "successful_pending_kyc", value: "successful_pending_kyc", display: <Badge className={`${statusClasses} status-successful_pending_kyc`}>Successful Pending KYC</Badge> },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const handleAccountClickDetails = (payoutRecord: any) => {
    const accountDetail = {
      id: payoutRecord.receiver_account?.id,
      asset: payoutRecord.asset,
      method_name: payoutRecord.method_name,
      method_details: payoutRecord.receiver_account?.method_details,
    };
    setReceiverAccountDetailToDisplay(accountDetail);
    setIsOpenReceiverAccountDetailModal(true);
  };

  const closeAccountDetailsModal = () => {
    setReceiverAccountDetailToDisplay(null);
    setIsOpenReceiverAccountDetailModal(false);
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const AmountRenderer = (params: ICellRendererParams) =>
    params.data.crypto ? (
      <>{params.data.amount}</>
    ) : (
      <DisplayAmount
        amount={Number(params.data.amount)}
        currency={params.data.asset}
      />
    );

  const AccountDetailRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Account Details" arrow placement="right">
      <div>
        <IconButton
          id={"button-details-" + params.data?.id}
          onClick={() => handleAccountClickDetails(params.data)}
          size="small"
        >
          <AiOutlineInfoCircle className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.status;
    const statusItem = statusList.find((item) => item.status === status);
    const failMessage = params.data.cause?.fail?.message || (typeof params.data.cause?.fail === "string" ? params.data.cause.fail : null);
    if (
      userRole === "superadmin" &&
      status === "failed" &&
      failMessage
    ) {
      return (
        <Tooltip title={failMessage} arrow placement="top">
          <div className="cursor-help">
            {statusItem ? statusItem.display : null}
          </div>
        </Tooltip>
      );
    }
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const columns: ColDef[] = [
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      width: 160,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "amount",
      headerName: "Amount",
      sortable: false,
      width: 100,
      cellRenderer: AmountRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 70,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "account_detail",
      headerName: "Account",
      sortable: false,
      width: 90,
      cellRenderer: AccountDetailRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "note",
      headerName: "Note",
      sortable: false,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "reference",
      headerName: "Reference",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "reference_tracking",
      headerName: "Ref. Tracking",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 150,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <div className="p-8">
      {receiverAccountDetailToDisplay && (
        <ReceiverAccountDetailsModal
          detail={receiverAccountDetailToDisplay}
          isOpen={isOpenReceiverAccountDetailModal}
          onClose={() => closeAccountDetailsModal()}
        />
      )}
      <Box>
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>
    </div>
  );
};

export default ReceiverPayoutHistory;
